import React, { ChangeEvent } from "react";

import { Select, SelectOptions } from "@at/design-system-elements";
import { TypeInsignia } from "@at/design-system-foundation";

import { useQueryParameters } from "@at/hooks";

import { useAppContext } from "@at/sauron-platform-core";

import {
  setBusinessTypeError,
  setSelectedBusinessType,
} from "../../../../../contexts/leasing/actions/leasing.actions";
import {
  useLeasingDispatchContext,
  useLeasingStateContext,
} from "../../../../../contexts/leasing/context/leasing.context";
import { ELeasingBusinessType } from "../../../../../contexts/leasing/types/leasing.types";
import { AbTestNames } from "../../../../../utils/ab-test/ab-test.util";
import { getBusinessType } from "../../../../../utils/leasing/getBusinessType/get-business-type";
import { useTracking } from "../../../../../utils/tracking/use-tracking";
import { getLeasingConfigEntity } from "../../../../../utils/tracking-util";
import {
  LeasingProductBordered,
  LeasingProductFormField,
  LeasingBusinessTypeInfo,
  LeasingBusinessTypeInfoBanner,
} from "../../leasing-contract-customiser.styled";

export const LeasingBusinessType = () => {
  const { setQueryParamsAndHistory } = useQueryParameters();
  const dispatch = useLeasingDispatchContext();
  const state = useLeasingStateContext();
  const { trackEvent } = useTracking();
  const { selectedBusinessType, businessTypeError } = state;
  const isDefault = selectedBusinessType === ELeasingBusinessType.DEFAULT;
  const hasBusinessTypeError = businessTypeError && isDefault;
  const leasingEntity = getLeasingConfigEntity(state);
  const { matchesTestGroups } = useAppContext();

  const isUserInLimitedCheckoutTest = matchesTestGroups(
    AbTestNames.LEASING_LIMITED_CHECKOUT,
  );

  const businessTypeOptions: SelectOptions[] = [
    {
      displayValue: "Please choose an option",
      value: ELeasingBusinessType.DEFAULT,
    },
    { displayValue: "Sole trader", value: ELeasingBusinessType.SOLE_TRADER },
    {
      displayValue: "Limited company",
      value: ELeasingBusinessType.LIMITED_COMPANY,
    },
    {
      displayValue: "Limited liability partnership",
      value: ELeasingBusinessType.LIMITED_LIABILITY_PARTNERSHIP,
    },
  ];

  const handleSelectedBusinessType = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedBusinessType = event.currentTarget.value;
    dispatch(setSelectedBusinessType(selectedBusinessType));
    dispatch(setBusinessTypeError(false));
    setQueryParamsAndHistory(
      {
        businessType: selectedBusinessType,
      },
      true,
    );

    trackEvent({
      trackingLabel: `leasing-business-type-selection`,
      replaceEntities: [
        {
          ...leasingEntity,
          lease_sub_type: getBusinessType(
            selectedBusinessType as ELeasingBusinessType,
          ),
        },
      ],
    });
  };

  return (
    <LeasingProductBordered id="business-type">
      <TypeInsignia as="h2" weight="400">
        Select your business type
      </TypeInsignia>
      <LeasingProductFormField>
        <Select
          id="business-type-dropdown"
          name="business-type"
          label="Type of business"
          onChange={(event) => handleSelectedBusinessType(event)}
          error={hasBusinessTypeError}
          errorText="Please select your business type"
          options={businessTypeOptions}
          aria-describedby="business type"
          value={selectedBusinessType}
          data-testid="business-type-select"
        />
        {selectedBusinessType === ELeasingBusinessType.SOLE_TRADER ? (
          <LeasingBusinessTypeInfo>
            If you are self-employed and run your own business then you are a
            &apos;Sole trader&apos;.
          </LeasingBusinessTypeInfo>
        ) : (
          selectedBusinessType !== ELeasingBusinessType.DEFAULT &&
          !isUserInLimitedCheckoutTest && (
            <LeasingBusinessTypeInfoBanner variation="info" type="in-panel">
              Online application process coming soon.
            </LeasingBusinessTypeInfoBanner>
          )
        )}
      </LeasingProductFormField>
    </LeasingProductBordered>
  );
};
